import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { duettOrderStatusLabel } from '../../lib/duettSaleStatus';

function formatAmount(amount) {
    if (amount === null || amount === undefined || amount === '') return '-';
    return util.format(amount, 0, ',', ' ');
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    if (typeof dateStr === 'string' && dateStr.length >= 10) {
        const parts = dateStr.substring(0, 10).split('-');
        if (parts.length === 3) {
            return `${parts[2]}.${parts[1]}.${parts[0]}`;
        }
    }
    return dateStr;
}

// Duett OrderStatus -> badge. No Duett order status means "paid": invoiceBooked is
// bokført (posted), reached a second after creation — never render it as «Betalt»
// (lib/duettSaleStatus.js duettOrderStatusLabel; backend PAID_STATUSES is empty).
function getOrderStatusBadge(status) {
    if (!status) return <span class='text-body-secondary'>-</span>;
    const label = duettOrderStatusLabel(status);
    if (status === 'invoiceBooked') {
        return <span class='badge bg-info text-dark' title='Bokført i Duett — sier ikke om fakturaen er betalt'>{label}</span>;
    }
    if (status === 'invoiceSent' || status === 'invoicePaymentProposal') {
        return <span class='badge bg-warning text-dark'>{label}</span>;
    }
    return <span class='badge bg-secondary'>{label}</span>;
}

@observer
class DuettComparison extends Component {
    constructor(props) {
        super(props);
        this.state = {
            filter: 'all',
            sortField: 'saleNumber',
            sortDirection: 'desc',
            searchText: '',
            expandedRows: {},
        };
    }

    setFilter = (filter) => {
        this.setState({ filter });
    }

    setSort = (field) => {
        this.setState(prev => ({
            sortField: field,
            sortDirection: prev.sortField === field && prev.sortDirection === 'asc' ? 'desc' : 'asc',
        }));
    }

    toggleRow = (key) => {
        this.setState(prev => ({
            expandedRows: {
                ...prev.expandedRows,
                [key]: !prev.expandedRows[key],
            },
        }));
    }

    getUnifiedRows() {
        const { stores } = this.props;
        const { duettStore } = stores;
        const data = duettStore.comparisonData;
        if (!data) return [];

        const rows = [];

        (data.matched || []).forEach(m => {
            rows.push({
                _type: 'matched',
                _key: `m-${m.saleId}-${m.orderId}`,
                saleId: m.saleId,
                saleNumber: m.saleNumber,
                orderId: m.orderId,
                invoiceNumber: m.invoiceNumber,
                orderStatus: m.orderStatus,
                localGross: m.localGross,
                duettGross: m.duettGross,
                diff: m.diff,
                hasAmountMismatch: false,
            });
        });

        (data.amountMismatch || []).forEach(m => {
            rows.push({
                _type: 'matched',
                _key: `mm-${m.saleId}-${m.orderId}`,
                saleId: m.saleId,
                saleNumber: m.saleNumber,
                orderId: m.orderId,
                invoiceNumber: m.invoiceNumber,
                orderStatus: m.orderStatus,
                localGross: m.localGross,
                duettGross: m.duettGross,
                diff: m.diff,
                hasAmountMismatch: true,
            });
        });

        (data.onlyLocal || []).forEach(m => {
            rows.push({
                _type: 'onlyLocal',
                _key: `l-${m.saleId}`,
                saleId: m.saleId,
                saleNumber: m.saleNumber,
                orderId: m.orderId,
                invoiceNumber: m.invoiceNumber,
                orderStatus: null,
                localGross: m.localGross,
                duettGross: null,
                diff: null,
                hasAmountMismatch: false,
            });
        });

        (data.onlyDuett || []).forEach(m => {
            rows.push({
                _type: 'onlyDuett',
                _key: `d-${m.orderId}`,
                saleId: null,
                saleNumber: null,
                orderId: m.orderId,
                orderNumber: m.orderNumber,
                invoiceNumber: m.invoiceNumber,
                orderStatus: m.status,
                localGross: null,
                duettGross: m.grossAmount,
                diff: null,
                hasAmountMismatch: false,
            });
        });

        return rows;
    }

    getFilteredSortedRows() {
        const { filter, sortField, sortDirection, searchText } = this.state;
        let rows = this.getUnifiedRows();

        // Filter
        if (filter === 'matched') {
            rows = rows.filter(r => r._type === 'matched' && !r.hasAmountMismatch);
        } else if (filter === 'mismatches') {
            rows = rows.filter(r => r._type === 'matched' && r.hasAmountMismatch);
        } else if (filter === 'onlyLocal') {
            rows = rows.filter(r => r._type === 'onlyLocal');
        } else if (filter === 'onlyDuett') {
            rows = rows.filter(r => r._type === 'onlyDuett');
        }

        // Search
        if (searchText) {
            const search = searchText.toLowerCase();
            rows = rows.filter(r => {
                const saleNum = String(r.saleNumber || '');
                const invoiceNum = String(r.invoiceNumber || '');
                return saleNum.includes(search) || invoiceNum.includes(search);
            });
        }

        // Sort
        rows.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            if (aVal === null || aVal === undefined) aVal = '';
            if (bVal === null || bVal === undefined) bVal = '';
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const aStr = String(aVal);
            const bStr = String(bVal);
            return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
        });

        return rows;
    }

    getRowClass(row) {
        if (row._type === 'onlyLocal') return 'table-info';
        if (row._type === 'onlyDuett') return 'table-danger';
        if (row.hasAmountMismatch) return 'table-warning';
        return '';
    }

    getStatusBadge(row) {
        if (row._type === 'onlyLocal') {
            return <span class='badge bg-info'>Kun lokal</span>;
        }
        if (row._type === 'onlyDuett') {
            return <span class='badge bg-danger'>Kun Duett</span>;
        }
        if (row.hasAmountMismatch) {
            return <span class='badge bg-warning text-dark'>Diff</span>;
        }
        return <span class='badge bg-success'>OK</span>;
    }

    renderSortIcon(field) {
        const { sortField, sortDirection } = this.state;
        if (sortField !== field) return null;
        return sortDirection === 'asc'
            ? <i class='fa-solid fa-sort-up ms-1' />
            : <i class='fa-solid fa-sort-down ms-1' />;
    }

    renderExpandedDetail(row) {
        return (
            <tr key={`${row._key}-detail`}>
                <td colspan='7' class='bg-body-secondary p-3'>
                    <div class='d-flex flex-column gap-2' style='font-size: 0.85em;'>
                        {row._type !== 'onlyDuett' && <>
                            <div>
                                <strong>Lokalt salg:</strong>{' '}
                                <a href={`/dyrejournal-sale/${row.saleId}`}>
                                    Apne salg #{row.saleNumber}
                                </a>
                            </div>
                            <div>
                                <strong>Lokalt beløp:</strong> {formatAmount(row.localGross)}
                            </div>
                        </>}
                        {row._type !== 'onlyLocal' && <>
                            <div>
                                <strong>Duett ordre:</strong> {row.orderId}
                                {row.orderNumber ? ` (#${row.orderNumber})` : ''}
                            </div>
                            <div>
                                <strong>Fakturanr:</strong> {row.invoiceNumber || '-'} |{' '}
                                <strong>Ordrestatus:</strong> {row.orderStatus || '-'} |{' '}
                                <strong>Duett beløp:</strong> {formatAmount(row.duettGross)}
                            </div>
                        </>}
                        {row._type === 'matched' && (
                            <div>
                                <strong>Differanse:</strong> {formatAmount(row.diff)}
                            </div>
                        )}
                    </div>
                </td>
            </tr>
        );
    }

    render() {
        const { stores, onClose, onRefresh } = this.props;
        const { duettStore } = stores;
        const { filter, searchText, expandedRows } = this.state;
        const data = duettStore.comparisonData;
        const loading = duettStore.comparisonLoading;

        if (loading) {
            return (
                <div class='d-flex flex-column align-items-center p-4'>
                    <i class='fa-duotone fa-spinner fa-spin fa-2x mb-3' />
                    <span>Henter data fra Duett og sammenligner...</span>
                </div>
            );
        }

        if (!data) {
            return (
                <div class='d-flex flex-column align-items-center p-4'>
                    <span class='text-body-secondary'>Ingen sammenligningsdata. Klikk "Sammenlign" for a starte.</span>
                </div>
            );
        }

        const { summary } = data;
        const rows = this.getFilteredSortedRows();
        const totalAll = summary.matchedCount + summary.mismatchCount + summary.onlyLocalCount + summary.onlyDuettCount;

        return (
            <div class='d-flex flex-column w-100'>
                {/* Header */}
                <div class='d-flex justify-content-between align-items-center mb-3'>
                    <h6 class='mb-0'>
                        <i class='fa-solid fa-code-compare me-2' />
                        Sammenligning: Dyrejournal vs Duett
                    </h6>
                    <div class='d-flex gap-2'>
                        <button class='btn btn-sm btn-outline-primary rounded-pill' onClick={onRefresh}>
                            <i class='fa-solid fa-refresh me-1' />Oppdater
                        </button>
                        <button class='btn btn-sm btn-outline-secondary rounded-pill' onClick={onClose}>
                            <i class='fa-solid fa-times me-1' />Lukk
                        </button>
                    </div>
                </div>

                {/* Summary badges */}
                <div class='d-flex flex-wrap gap-2 mb-3'>
                    <button
                        class={`btn btn-sm rounded-pill ${filter === 'all' ? 'btn-dark' : 'btn-outline-dark'}`}
                        onClick={() => this.setFilter('all')}
                    >
                        Alle ({totalAll})
                    </button>
                    <button
                        class={`btn btn-sm rounded-pill ${filter === 'matched' ? 'btn-success' : 'btn-outline-success'}`}
                        onClick={() => this.setFilter('matched')}
                    >
                        Matchet ({summary.matchedCount})
                    </button>
                    {summary.mismatchCount > 0 && (
                        <button
                            class={`btn btn-sm rounded-pill ${filter === 'mismatches' ? 'btn-warning' : 'btn-outline-warning'}`}
                            onClick={() => this.setFilter('mismatches')}
                        >
                            Differanser ({summary.mismatchCount})
                        </button>
                    )}
                    <button
                        class={`btn btn-sm rounded-pill ${filter === 'onlyLocal' ? 'btn-info' : 'btn-outline-info'}`}
                        onClick={() => this.setFilter('onlyLocal')}
                    >
                        Kun i Dyrejournal ({summary.onlyLocalCount})
                    </button>
                    {summary.onlyDuettCount > 0 && (
                        <button
                            class={`btn btn-sm rounded-pill ${filter === 'onlyDuett' ? 'btn-danger' : 'btn-outline-danger'}`}
                            onClick={() => this.setFilter('onlyDuett')}
                        >
                            Kun i Duett ({summary.onlyDuettCount})
                        </button>
                    )}
                </div>

                {/* Search failures / approximation note */}
                {data.searchFailures > 0 && (
                    <div class='alert alert-warning py-2 mb-2'>
                        <i class='fa-solid fa-triangle-exclamation me-1' />
                        {data.searchFailures} oppslag mot Duett feilet og er utelatt fra sammenligningen (ingen rader ble feilklassifisert).
                    </div>
                )}
                {data.note && (
                    <div class='mb-2 px-2'>
                        <small class='text-body-secondary'>
                            <i class='fa-solid fa-circle-info me-1' />
                            "Kun i Duett" er {data.note}.
                        </small>
                    </div>
                )}

                {/* Search */}
                <div class='mb-3 px-2'>
                    <input
                        type='text'
                        class='form-control form-control-sm'
                        placeholder='Sok etter salgsnr eller fakturanr...'
                        value={searchText}
                        onInput={(e) => this.setState({ searchText: e.target.value })}
                    />
                </div>

                {/* Date range info */}
                <div class='mb-2 px-2'>
                    <small class='text-body-secondary'>
                        Periode: {formatDate(data.dateFrom)} - {formatDate(data.dateTo)}
                    </small>
                </div>

                {/* Table */}
                <div class='table-responsive'>
                    <table class='table table-sm table-hover mb-0' style='font-size: 0.85em;'>
                        <thead>
                            <tr>
                                <th style='cursor: pointer;' onClick={() => this.setSort('_type')}>
                                    Status {this.renderSortIcon('_type')}
                                </th>
                                <th style='cursor: pointer;' onClick={() => this.setSort('saleNumber')}>
                                    Salg # {this.renderSortIcon('saleNumber')}
                                </th>
                                <th style='cursor: pointer;' onClick={() => this.setSort('invoiceNumber')}>
                                    Faktura # {this.renderSortIcon('invoiceNumber')}
                                </th>
                                <th class='text-end' style='cursor: pointer;' onClick={() => this.setSort('localGross')}>
                                    Lokalt {this.renderSortIcon('localGross')}
                                </th>
                                <th class='text-end' style='cursor: pointer;' onClick={() => this.setSort('duettGross')}>
                                    Duett beløp {this.renderSortIcon('duettGross')}
                                </th>
                                <th class='text-end' style='cursor: pointer;' onClick={() => this.setSort('diff')}>
                                    Diff {this.renderSortIcon('diff')}
                                </th>
                                <th>Duett status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <Fragment key={row._key}>
                                    <tr
                                        class={this.getRowClass(row)}
                                        style='cursor: pointer;'
                                        onClick={() => this.toggleRow(row._key)}
                                    >
                                        <td>{this.getStatusBadge(row)}</td>
                                        <td>{row.saleNumber || '-'}</td>
                                        <td>{row.invoiceNumber || '-'}</td>
                                        <td class='text-end'>{formatAmount(row.localGross)}</td>
                                        <td class='text-end'>{formatAmount(row.duettGross)}</td>
                                        <td class={`text-end ${row.hasAmountMismatch ? 'fw-bold text-warning' : ''}`}>
                                            {row.diff !== null && row.diff !== undefined ? formatAmount(row.diff) : '-'}
                                        </td>
                                        <td>{getOrderStatusBadge(row.orderStatus)}</td>
                                    </tr>
                                    {expandedRows[row._key] && this.renderExpandedDetail(row)}
                                </Fragment>
                            ))}
                            {rows.length === 0 && (
                                <tr>
                                    <td colspan='7' class='text-center text-body-secondary py-3'>
                                        Ingen rader a vise for valgt filter.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
}

export default DuettComparison;
